import { useMemo } from 'react';
/**
 * Sentinel / paired-page decisions for organic YMAL vs sponsored pagination.
 * Sponsored failures must not block organic loading or hold organic behind pairing.
 */

import {
    MAXIMUM_YMAL_ITEMS_COUNT,
    SOLD_PDP_YMAL_SL_INITIAL,
    SOLD_PDP_YMAL_SL_SUBSEQUENT,
    MORE_ML_ITEMS_FETCH_COUNT,
    ML_ITEMS_INITIAL_COUNT,
} from '../PdpRespSimilarItems/pdpRespSimilarItemsConstants.ts';

export function canLoadPdpSimilarItemsOrganicPage({
    hasNext,
    isLoadingNext,
    hasLoadError,
    organicItemsCount,
    maxOrganicItems = MAXIMUM_YMAL_ITEMS_COUNT,
}: {
    hasNext: boolean;
    isLoadingNext: boolean;
    hasLoadError: boolean;
    organicItemsCount: number;
    maxOrganicItems?: number;
}): boolean {
    return hasNext && !isLoadingNext && !hasLoadError && organicItemsCount < maxOrganicItems;
}

export function canLoadPdpSimilarItemsSponsoredPage({
    isEffectiveVariant,
    hasNext,
    hasNextSponsored,
    isLoadingNextSponsored,
    hasSponsoredLoadError,
}: {
    isEffectiveVariant: boolean;
    hasNext: boolean;
    hasNextSponsored: boolean;
    isLoadingNextSponsored: boolean;
    hasSponsoredLoadError: boolean;
}): boolean {
    return (
        isEffectiveVariant &&
        hasNext &&
        hasNextSponsored &&
        !isLoadingNextSponsored &&
        !hasSponsoredLoadError
    );
}

/**
 * Gate display to paired pages while the variant is still pairing SL inventory.
 * Once SL pagination is exhausted, failed, or organic has no more pages, show
 * every fetched organic tile (capped) — a short SL pool must not hide ML.
 */
function getVisiblePdpSimilarItems<TOrganic, TSponsored>({
    organicItems,
    sponsoredItems,
    organicPagesLoaded,
    sponsoredPagesLoaded,
    isEffectiveVariant,
    hasSponsoredLoadError,
    isLoadingNextSponsored,
    hasNext,
    hasNextSponsored,
    maxOrganicItems = MAXIMUM_YMAL_ITEMS_COUNT,
    organicInitialCount = ML_ITEMS_INITIAL_COUNT,
    organicSubsequentCount = MORE_ML_ITEMS_FETCH_COUNT,
    sponsoredInitialCount = SOLD_PDP_YMAL_SL_INITIAL,
    sponsoredSubsequentCount = SOLD_PDP_YMAL_SL_SUBSEQUENT,
}: {
    organicItems: readonly TOrganic[];
    sponsoredItems: readonly TSponsored[];
    organicPagesLoaded: number;
    sponsoredPagesLoaded: number;
    isEffectiveVariant: boolean;
    hasSponsoredLoadError: boolean;
    isLoadingNextSponsored: boolean;
    hasNext: boolean;
    hasNextSponsored: boolean;
    maxOrganicItems?: number;
    organicInitialCount?: number;
    organicSubsequentCount?: number;
    sponsoredInitialCount?: number;
    sponsoredSubsequentCount?: number;
}): {
    organicItems: readonly TOrganic[];
    sponsoredItems: readonly TSponsored[];
} {
    if (!isEffectiveVariant) {
        return { organicItems, sponsoredItems };
    }

    const isWaitingForSponsoredPage =
        !hasSponsoredLoadError && (isLoadingNextSponsored || (hasNext && hasNextSponsored));

    if (!isWaitingForSponsoredPage) {
        return {
            organicItems: organicItems.slice(0, maxOrganicItems),
            sponsoredItems,
        };
    }

    const completedPages = Math.min(organicPagesLoaded, sponsoredPagesLoaded);

    return {
        organicItems: organicItems.slice(
            0,
            Math.min(maxOrganicItems, organicInitialCount + completedPages * organicSubsequentCount)
        ),
        sponsoredItems: sponsoredItems.slice(
            0,
            sponsoredInitialCount + completedPages * sponsoredSubsequentCount
        ),
    };
}

export function useVisiblePdpSimilarItems<TOrganic, TSponsored>(args: {
    organicItems: readonly TOrganic[];
    sponsoredItems: readonly TSponsored[];
    organicPagesLoaded: number;
    sponsoredPagesLoaded: number;
    isEffectiveVariant: boolean;
    hasSponsoredLoadError: boolean;
    isLoadingNextSponsored: boolean;
    hasNext: boolean;
    hasNextSponsored: boolean;
    maxOrganicItems?: number;
    organicInitialCount?: number;
    organicSubsequentCount?: number;
    sponsoredInitialCount?: number;
    sponsoredSubsequentCount?: number;
}): {
    organicItems: readonly TOrganic[];
    sponsoredItems: readonly TSponsored[];
} {
    return useMemo(() => getVisiblePdpSimilarItems(args), [args]);
}
